<template>
  <v-container v-if="isReady" :style="{ maxWidth: screenWidth }">
    <v-card class="mt-2">
      <base-card-title
        label="Logistic Lead Times without Terms of Delivery coverage"
        icon="mdi-alert-circle-outline"/>
      <v-card-text class="pl-8 pr-8 pb-4">
        <p class="text--secondary mb-3">
          Lists all currently published, active Logistic Lead Times that have no active Terms of Delivery
          satisfying all three matching conditions: country of delivery, mode of transport and destination
          logistics country.
        </p>
        <v-row class="pt-3">
          <v-col cols="3" class="pb-0 pt-0">
            <base-input-select-date-picker
              item-label="Active date"
              :selected-value="criteria.activeDate"
              @change="criteria.activeDate = arguments[0]"/>
          </v-col>
          <v-col cols="3" class="pb-0 pt-0">
            <base-input-multi-select-with-flag
              :items="geographicalCountries"
              item-value="id"
              item-text="description"
              item-country="code"
              item-label="Country of delivery"
              :selected-values="criteria.countryOfDeliveryIds"
              @change="criteria.countryOfDeliveryIds = arguments[0]"/>
          </v-col>
          <v-col cols="3" class="pb-0 pt-0">
            <base-input-multi-select-with-flag
              :items="geographicalCountries"
              item-value="id"
              item-text="description"
              item-country="code"
              item-label="Destination country"
              :selected-values="criteria.destinationLogisticsCountryIds"
              @change="criteria.destinationLogisticsCountryIds = arguments[0]"/>
          </v-col>
          <v-col cols="3" class="pb-0 pt-0">
            <base-input-multi-select-with-freight-service
              :grouped-freight-services="groupedFreightServices"
              :freight-services="freightServices"
              item-label="Freight service"
              :selected-value="criteria.freightServiceIds"
              @change="criteria.freightServiceIds = arguments[0]"/>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <div class="mt-2">
      <base-input-search-and-clear-and-download-buttons
        @search="load"
        @reset="reset"
        @download="downloadExcel"/>
    </div>

    <v-card class="mt-5">
      <v-data-table
        dense
        :headers="tableHeaders"
        :items="items"
        :loading="loading"
        :items-per-page="50"
        :footer-props="{ itemsPerPageOptions: [10, 25, 50, 100] }">

        <template v-slot:item.shippingLtFrom="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <base-label-with-flag
                  size="small"
                  :item-country="item.shippingLeadTime.placeOfLoading.location.code"
                  :item-label="item.shippingLeadTime.placeOfLoading.unlocode"/>
              </span>
            </template>
            <span>{{ item.shippingLeadTime.placeOfLoading.name }}, {{ item.shippingLeadTime.placeOfLoading.location.name }}</span>
          </v-tooltip>
        </template>

        <template v-slot:item.shippingLtTo="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <base-label-with-flag
                  size="small"
                  :item-country="item.shippingLeadTime.placeOfDelivery.location.code"
                  :item-label="item.shippingLeadTime.placeOfDelivery.unlocode"/>
              </span>
            </template>
            <span>{{ item.shippingLeadTime.placeOfDelivery.name }}, {{ item.shippingLeadTime.placeOfDelivery.location.name }}</span>
          </v-tooltip>
        </template>

        <template v-slot:item.shippingMoT="{ item }">
          <base-label-with-freight-service :freight-service="item.shippingLeadTime.freightService"/>
        </template>

        <template v-slot:item.finalDestination="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <base-label-with-flag
                  size="small"
                  :item-country="item.finalDestinationLeadTime.location.location.code"
                  :item-label="item.finalDestinationLeadTime.location.unlocode"/>
              </span>
            </template>
            <span>{{ item.finalDestinationLeadTime.location.name }}, {{ item.finalDestinationLeadTime.location.location.name }}</span>
          </v-tooltip>
        </template>

        <template v-slot:item.planningMarkets="{ item }">
          <base-planning-market-with-tool-tip
            :planning-markets="item.finalDestinationLeadTime.planningMarketsActiveOnCurrentDate"
            :threshold="5"
            size="small"/>
        </template>

        <template v-slot:item.transitLt="{ item }">
          <a v-if="hasTransitLeadTime(item)" @click="showTransitView(item)" title="View transit details">Yes</a>
          <span v-else>No</span>
        </template>

        <template v-slot:item.haulageLt="{ item }">
          <a v-if="hasHaulageLeadTime(item)" @click="showHaulageView(item)" title="View haulage details">Yes</a>
          <span v-else>No</span>
        </template>

        <template v-slot:item.serviceProvider="{ item }">
          <label>{{ item.shippingLeadTime.serviceProvider.code }}</label>
        </template>

        <template v-slot:item.validTo="{ item }">
          <span>{{ item.validTo }}</span>
        </template>

        <template v-slot:item.actions="{ item }">
          <base-tool-tip
            label="View detail"
            icon="mdi-magnify"
            @click="showViewDialog(item)"/>
        </template>

      </v-data-table>
    </v-card>

    <search-logistic-lead-time-view-details-dialog
      :can-update="false"
      :viewDialog="viewDialog"
      :viewDialogLogisticLeadTime="viewDialogLlt"
      @close="viewDialog = false"/>

    <search-transit-lead-time-view-details-dialog
      :view-dialog="viewTransitDialog"
      :view-dialog-transit-lead-time="transitInContext"
      :can-update="false"
      @close="viewTransitDialog = false"/>

    <search-haulage-lead-time-view-details-dialog
      :view-dialog="viewHaulageDialog"
      :view-dialog-haulage-lead-time="haulageInContext"
      :can-update="false"
      @close="viewHaulageDialog = false"/>

  </v-container>
</template>

<script>
import axios from 'axios'
import BaseCardTitle from '@/base/card/BaseCardTitle'
import BaseLabelWithFlag from '@/base/label/BaseLabelWithFlag'
import BaseLabelWithFreightService from '@/base/label/BaseLabelWithFreightService'
import BasePlanningMarketWithToolTip from '@/base/tooltip/BasePlanningMarketWithToolTip'
import BaseInputSearchAndClearAndDownloadButtons from '@/base/input/BaseInputSearchAndClearAndDownloadButtons'
import BaseInputSelectDatePicker from '@/base/input/BaseInputSelectDatePicker'
import BaseInputMultiSelectWithFlag from '@/base/input/BaseInputMultiSelectWithFlag'
import BaseInputMultiSelectWithFreightService from '@/base/input/BaseInputMultiSelectWithFreightService'
import BaseToolTip from '@/base/tooltip/BaseToolTip'
import SearchLogisticLeadTimeViewDetailsDialog
  from '@/components/logisticsleadtime/searchlogisticleadtime/SearchLogisticLeadTimeViewDetailsDialog'
import SearchTransitLeadTimeViewDetailsDialog
  from '@/components/transitleadtime/searchtransitleadtime/SearchTransitLeadTimeViewDetailsDialog'
import SearchHaulageLeadTimeViewDetailsDialog
  from '@/components/haulageleadtime/searchhaulageleadtime/SearchHaulageLeadTimeViewDetailsDialog'
import { downloadBlobMixin } from '@/mixins/downloadBlobMixin'
import { arraysUtils } from '@/mixins/utilsMixin'
import { globalMixin } from '@/store/globalMixin'
import { mapGetters } from 'vuex'

export default {
  name: 'LltWithoutTermsOfDeliveryCoverage',
  components: {
    BaseCardTitle,
    BaseLabelWithFlag,
    BaseLabelWithFreightService,
    BasePlanningMarketWithToolTip,
    BaseInputSearchAndClearAndDownloadButtons,
    BaseInputSelectDatePicker,
    BaseInputMultiSelectWithFlag,
    BaseInputMultiSelectWithFreightService,
    BaseToolTip,
    SearchLogisticLeadTimeViewDetailsDialog,
    SearchTransitLeadTimeViewDetailsDialog,
    SearchHaulageLeadTimeViewDetailsDialog
  },
  mixins: [globalMixin, arraysUtils, downloadBlobMixin],
  data () {
    return {
      loading: false,
      items: [],
      criteria: {
        activeDate: new Date().toISOString().split('T')[0],
        countryOfDeliveryIds: [],
        destinationLogisticsCountryIds: [],
        freightServiceIds: []
      },
      viewDialog: false,
      viewTransitDialog: false,
      viewHaulageDialog: false,
      viewDialogLlt: {},
      transitInContext: undefined,
      haulageInContext: undefined,
      tableHeaders: [
        { text: 'Shipping from', align: 'start', sortable: true, value: 'shippingLtFrom', width: '8%' },
        { text: 'Shipping to', align: 'start', sortable: true, value: 'shippingLtTo', width: '8%' },
        { text: 'Shipping MoT', align: 'start', sortable: true, value: 'shippingMoT', width: '10%' },
        { text: 'Final destination', align: 'start', sortable: true, value: 'finalDestination', width: '8%' },
        { text: 'Destination Planning Market(s)', align: 'start', sortable: false, value: 'planningMarkets', width: '28%' },
        { text: 'Transit', align: 'start', sortable: false, value: 'transitLt', width: '5%' },
        { text: 'Haulage', align: 'start', sortable: false, value: 'haulageLt', width: '5%' },
        { text: 'TSP', align: 'start', sortable: true, value: 'serviceProvider', width: '5%' },
        { text: 'Valid from', align: 'start', sortable: true, value: 'validFrom', width: '8%' },
        { text: 'Valid to', align: 'start', sortable: true, value: 'validTo', width: '8%' },
        { text: 'Actions', sortable: false, value: 'actions', width: '4%' }
      ]
    }
  },
  computed: {
    ...mapGetters(['geographicalCountries', 'freightServices', 'groupedFreightServices']),
    isReady () {
      return this.freightServices.length > 0 &&
             this.groupedFreightServices.length > 0 &&
             this.geographicalCountries.length > 0
    }
  },
  mounted () {
    this.$store.dispatch('getAllGeographicalCountries')
    this.$store.dispatch('getAllFreightServices')
    this.$store.dispatch('getAllGroupedFreightServices')
  },
  methods: {
    load () {
      this.loading = true
      axios.post('/api/touchpoint/logistic-lead-time/without-tod-coverage',
        this.removeEmptyArrays({ ...this.criteria })
      )
        .then(response => {
          this.items = response.data
        })
        .catch(e => console.error('Failed to load LLT without ToD coverage', e))
        .finally(() => {
          this.loading = false
        })
    },
    reset () {
      this.items = []
      this.criteria = {
        activeDate: new Date().toISOString().split('T')[0],
        countryOfDeliveryIds: [],
        destinationLogisticsCountryIds: [],
        freightServiceIds: []
      }
    },
    hasTransitLeadTime (item) {
      return !!item.transitLeadTime
    },
    hasHaulageLeadTime (item) {
      return !!item.haulageLeadTime
    },
    showViewDialog (item) {
      this.viewDialogLlt = item
      this.viewDialog = true
    },
    showTransitView (item) {
      this.transitInContext = item.transitLeadTime
      this.viewTransitDialog = true
    },
    showHaulageView (item) {
      this.haulageInContext = item.haulageLeadTime
      this.viewHaulageDialog = true
    },
    async downloadExcel () {
      try {
        const response = await axios.post('/api/touchpoint/logistic-lead-time/without-tod-coverage/download.xlsx',
          this.removeEmptyArrays({ ...this.criteria }),
          { responseType: 'blob' }
        )
        this.downloadBlobResponse(response, 'LltWithoutTermsOfDeliveryCoverage.xlsx')
      } catch (e) {
        console.error('Failed to download LLT without ToD coverage Excel', e)
      }
    }
  }
}
</script>

<style scoped>
.container {
  max-width: 100%;
}

.v-icon.v-icon {
  font-size: 20px !important;
  margin-right: 0px !important;
}
</style>
